import { ReportData } from '@/types/report';
import { DateTime } from 'luxon';

/**
 * Format date to Thai locale format (dd/mm/yyyy)
 */
export const formatDate = (dateString: string | undefined): string => {
  if (!dateString) return '';
  try {
    const date = DateTime.fromISO(dateString).setZone('Asia/Bangkok');
    return date.toFormat('dd/MM/yyyy');
  } catch (error) {
    console.error('Date formatting error:', error);
    return dateString || '';
  }
};

/**
 * Format time from date string (HH:mm) in Bangkok timezone
 */
export const formatTime = (dateString: string | undefined): string => {
  if (!dateString) return '';
  try {
    const date = DateTime.fromISO(dateString).setZone('Asia/Bangkok');
    return date.toFormat('HH:mm');
  } catch (error) {
    console.error('Time formatting error:', error);
    return '';
  }
};

/**
 * Extract problems encountered as a comma-separated string
 */
export const getProblemsEncountered = (report: ReportData | null): string => {
  if (!report) return '-';
  
  // First try to get from parsed development report
  const developmentData = parseDevelopmentReport(report);
  if (developmentData?.problems_encountered) {
    const problems = developmentData.problems_encountered;
    let problemsString = Array.isArray(problems) ? problems.join(', ') : problems;
    
    // Add "other" problems if provided
    if (developmentData.problems_encountered_other) {
      problemsString += problemsString ? `, ${developmentData.problems_encountered_other}` : developmentData.problems_encountered_other;
    }
    
    return problemsString || 'ไม่มี';
  }
  
  // Fallback to original fields
  const problems = report.problems_encountered || [];
  let problemsString = problems.join(', ');
  
  // Add "other" problems if provided
  if (report.problems_encountered_other) {
    problemsString += problemsString ? `, ${report.problems_encountered_other}` : report.problems_encountered_other;
  }
  
  return problemsString || 'ไม่มี';
};

/**
 * Extract solutions applied as a comma-separated string
 */
export const getSolutionsApplied = (report: ReportData | null): string => {
  if (!report) return '-';
  
  // First try to get from parsed development report
  const developmentData = parseDevelopmentReport(report);
  if (developmentData?.solutions_applied) {
    const solutions = developmentData.solutions_applied;
    let solutionsString = Array.isArray(solutions) ? solutions.join(', ') : solutions;
    
    // Add "other" solutions if provided
    if (developmentData.solutions_applied_other) {
      solutionsString += solutionsString ? `, ${developmentData.solutions_applied_other}` : developmentData.solutions_applied_other;
    }
    
    return solutionsString || 'ไม่มี';
  }
  
  // Fallback to original fields
  const solutions = report.solutions_applied || [];
  let solutionsString = solutions.join(', ');
  
  // Add "other" solutions if provided
  if (report.solutions_applied_other) {
    solutionsString += solutionsString ? `, ${report.solutions_applied_other}` : report.solutions_applied_other;
  }
  
  return solutionsString || 'ไม่มี';
};

/**
 * Parse development report JSON if it exists
 */
export const parseDevelopmentReport = (report: ReportData | null) => {
  if (!report || !report.development_report || report.development_report === 'นักเรียนไม่เข้าเรียน') return null;
  
  try {
    return JSON.parse(report.development_report);
  } catch (error) {
    console.error('Error parsing development report:', error);
    return null;
  }
};

/**
 * Get specific field from either parsed development report or direct field
 */
export const getReportField = (report: ReportData | null, fieldName: string): string => {
  if (!report) return '-';
  
  // First try to get from parsed development report
  const developmentData = parseDevelopmentReport(report);
  if (developmentData && developmentData[fieldName] !== undefined) {
    return developmentData[fieldName] || '-';
  }
  
  // Then try to get directly from report
  if (report[fieldName as keyof ReportData] !== undefined) {
    const value = report[fieldName as keyof ReportData];
    return typeof value === 'string' ? value : '-';
  }
  
  return '-';
};

/**
 * Generate a formatted text report for sharing
 */
export const generateReportText = (report: ReportData | null): string => {
  if (!report) return '';

  // Get development report data
  const developmentData = parseDevelopmentReport(report);

  // Use start_date for class information if available
  const classDate = report.start_date ? formatDate(report.start_date) : formatDate(report.teaching_date);
  const classTime = report.start_date ? formatTime(report.start_date) : report.teaching_time_slot;
  const endTime = report.end_date ? formatTime(report.end_date) : '';
  
  // Get topic learned from either development report or direct field
  const topicLearned = developmentData?.topic_learned || report.topic_learned || '-';
  
  // Get ratings from either development report or direct fields
  const knowledgeRating = developmentData?.knowledge_rating || report.knowledge_rating || '-';
  const knowledgeBehavior = developmentData?.knowledge_behavior || report.knowledge_behavior || '-';
  const skillsRating = developmentData?.skills_rating || report.skills_rating || '-';
  const skillsBehavior = developmentData?.skills_behavior || report.skills_behavior || '-';
  const attitudeRating = developmentData?.attitude_rating || report.attitude_rating || '-';
  const attitudeBehavior = developmentData?.attitude_behavior || report.attitude_behavior || '-';
  const studentImprovement = developmentData?.student_improvement || report.student_improvement || '-';

  return `แจ้งพัฒนาการเรียนของ: น้อง${report.student_nickname} :${report.student_full_name}

📅 วันที่: ${classDate} เวลา ${classTime} - ${endTime}
📚 วิชา: ${report.subject}
👩‍🏫 ครูผู้สอน: ${report.teacher_display || report.teacher_id || '-'}
✏️ หัวข้อบทเรียน: ${topicLearned}
📌 ระยะเวลาเรียน: ${report.class_duration || '-'} ชั่วโมง
📌 ระดับพัฒนาการของน้องใน 3 มิติ

🔹 ระดับพัฒนาการมิติความรู้ (Knowledge): ${knowledgeRating}
- พฤติกรรมที่สะท้อนถึง: ${knowledgeBehavior}
🔹 ระดับพัฒนาการมิติทักษะ (Skills): ${skillsRating}
- พฤติกรรมที่สะท้อนถึง: ${skillsBehavior}
🔹 ระดับพัฒนาการทัศนคติ (Attitude): ${attitudeRating}
- พฤติกรรมที่สะท้อนถึง: ${attitudeBehavior}

📌 สำหรับวันนี้น้องมีพัฒนาการที่เพิ่มขึ้นคือ: ${studentImprovement}

📌 ปัญหาที่เกิดกับน้องในการเรียน: ${getProblemsEncountered(report)}

📌 แนวทางที่ใช้แก้ไขในคลาส: ${getSolutionsApplied(report)}

📌 การบ้าน / แบบฝึกหัดที่ให้น้องวันนี้: ${report.homework_assigned || report.homework_assignment || '-'}

📌 เรื่องถัดไปที่จะเรียน: ${report.next_topic || report.next_class_preparation || '-'}

รายงานฉบับนี้เป็นการสรุปพัฒนาการเรียนรู้ของน้องจากคุณครู เพื่อให้ผู้ปกครองสามารถติดตามความก้าวหน้าของบุตรหลานได้ หากมีข้อสงสัยเกี่ยวกับการเรียนหรืออยากพูดคุยเพิ่มเติม สามารถแจ้งครูได้เสมอนะคะ/ครับ 😊`;
};
